import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import DashboardLayout from '../../components/layout/DashboardLayout';
import SEO from '../../components/SEO';
import { Headphones, Play, Pause, Lock, Music, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { logActivity } from '../../utils/activity';
import { hasAudioAccess } from '../../utils/subscription';
import { useTranslation } from 'react-i18next';

const supabase = createClient(
    import.meta.env.VITE_PUBLIC_SUPABASE_URL,
    import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Audio() {
    const { t } = useTranslation();
    const [user, setUser] = useState<any>(null);
    const [audios, setAudios] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [subscription, setSubscription] = useState<any>(null);
    const [currentAudio, setCurrentAudio] = useState<any>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [completedAudios, setCompletedAudios] = useState<any[]>([]);
    const [audioElement] = useState(new window.Audio());

    const fetchSubscription = useCallback(async (userId: string) => {
        try {
            const { data } = await supabase
                .from('subscriptions')
                .select('*')
                .eq('user_id', userId)
                .eq('status', 'active')
                .single();
            setSubscription(data);
        } catch (error) {
            console.error('Error fetching subscription:', error);
        }
    }, []);

    const fetchAudios = useCallback(async () => {
        try {
            const { data, error } = await supabase.storage.from('audios').list();
            if (error) {
                console.error('Error fetching audios:', error);
                return;
            }
            // Filter out non-mp3 files and sort alphabetically
            setAudios(data.filter(file => file.name.endsWith('.mp3')).sort((a, b) => a.name.localeCompare(b.name)));
        } catch (error) {
            console.error('Supabase storage error:', error);
        }
    }, []);

    const checkAuth = useCallback(async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
            window.REACT_APP_NAVIGATE('/login');
            return;
        }
        setUser(user);
        setCompletedAudios(user.user_metadata?.completed_audios || []);
        await Promise.all([
            fetchSubscription(user.id),
            fetchAudios()
        ]);
        setLoading(false);
    }, [fetchAudios, fetchSubscription]);

    useEffect(() => {
        checkAuth();
        return () => {
            audioElement.pause();
            audioElement.src = '';
        };
    }, [audioElement, checkAuth]);

    const isPremium = hasAudioAccess(subscription);

    const togglePlay = (audio: any) => {
        if (audios.length === 0) return;

        const isLocked = !isPremium;

        if (isLocked) {
            window.REACT_APP_NAVIGATE('/pricing');
            return;
        }

        if (currentAudio?.name === audio.name) {
            if (isPlaying) {
                audioElement.pause();
                setIsPlaying(false);
            } else {
                audioElement.play();
                setIsPlaying(true);
            }
        } else {
            const { data } = supabase.storage.from('audios').getPublicUrl(audio.name);
            audioElement.src = data.publicUrl;
            audioElement.play().catch(err => console.error("Playback error:", err));
            setCurrentAudio(audio);
            setIsPlaying(true);
        }
    };

    const formatTime = (time: number) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const time = parseFloat(e.target.value);
        audioElement.currentTime = time;
        setCurrentTime(time);
    };

    // Add event listeners for audio status
    useEffect(() => {
        const audio = audioElement;

        const saveAudioActivity = async (audioName: string, isCompleted: boolean = false) => {
            if (!user) return;

            const now = new Date().toISOString();
            const existingActivities = [...(user.user_metadata?.completed_audios || [])];

            // Find existing entry for this audio
            const existingIndex = existingActivities.findIndex(item =>
                typeof item === 'string' ? item === audioName : item.name === audioName
            );

            let updatedActivities;
            if (existingIndex > -1) {
                // Update existing activity
                const existing = typeof existingActivities[existingIndex] === 'string'
                    ? { name: existingActivities[existingIndex] }
                    : existingActivities[existingIndex];

                updatedActivities = [...existingActivities];
                updatedActivities[existingIndex] = {
                    ...existing,
                    date: now,
                    // Only set completed to true if it was already true or is now true
                    completed: existing.completed || isCompleted
                };
            } else {
                // Add new activity
                updatedActivities = [...existingActivities, {
                    name: audioName,
                    date: now,
                    completed: isCompleted,
                    type: 'audio'
                }];
            }

            setCompletedAudios(updatedActivities);

            // Save to user metadata
            const { error } = await supabase.auth.updateUser({
                data: {
                    ...user.user_metadata,
                    completed_audios: updatedActivities
                }
            });

            if (error) console.error('Error saving audio activity:', error);
        };

        const handleEnd = async () => {
            setIsPlaying(false);
            if (currentAudio) {
                await saveAudioActivity(currentAudio.name, true);

                // Log activity
                logActivity({
                    type: 'audio',
                    title: currentAudio.name.replace('.mp3', '').replace(/^\d+[-_]/, '').replaceAll('-', ' '),
                    subtitle: t('audio.status.finished'),
                    value: '100%'
                });
            }
        };

        const handlePlay = async () => {
            if (currentAudio) {
                await saveAudioActivity(currentAudio.name, false);

                // Log activity as soon as it starts
                logActivity({
                    type: 'audio',
                    title: currentAudio.name.replace('.mp3', '').replace(/^\d+[-_]/, '').replaceAll('-', ' '),
                    subtitle: t('audio.status.listening_now'),
                    value: t('audio.status.playing')
                });
            }
        };

        const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
        const handleLoadedMetadata = () => setDuration(audio.duration);

        audio.addEventListener('ended', handleEnd);
        audio.addEventListener('play', handlePlay);
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);

        return () => {
            audio.removeEventListener('ended', handleEnd);
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        };
    }, [audioElement, currentAudio, user, t]);



    if (loading) {
        return (
            <DashboardLayout user={user}>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout user={user}>
            <SEO noindex={true} />
            <div className="max-w-4xl mx-auto pb-24">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-text-main mb-2 flex items-center gap-3">
                        <Headphones className="text-primary" size={32} />
                        {t('audio.title')}
                    </h1>
                    <p className="text-text-muted">{t('audio.subtitle')}</p>
                </div>



                {/* Audio Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {audios.map((audio) => {
                        const isPlayingThis = currentAudio?.name === audio.name && isPlaying;
                        const isLocked = !isPremium;
                        // Clean up title: remove extension, replace dashes/underscores with spaces
                        const title = audio.name
                            .replace('.mp3', '')
                            .replace(/^\d+[-_]/, '') // Remove leading numbers
                            .replaceAll('-', ' ')
                            .replaceAll('_', ' ');

                        return (
                            <div
                                key={audio.name}
                                className={`p-5 rounded-2xl border transition-all duration-300 ${isPlayingThis
                                    ? 'bg-primary/10 border-primary shadow-[0_0_20px_rgba(220,38,38,0.1)]'
                                    : 'bg-dark-card border-dark-border hover:border-primary/30 hover:bg-dark-hover'
                                    } flex items-center justify-between group cursor-pointer`}
                                onClick={() => togglePlay(audio)}
                            >
                                <div className="flex items-center gap-4 min-w-0">
                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300 ${isPlayingThis ? 'bg-primary text-white scale-110' : 'bg-dark-bg text-text-muted group-hover:text-primary'
                                        }`}>
                                        {isLocked ? <Lock size={20} /> : <Music size={20} />}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h3 className={`font-bold truncate capitalize ${isPlayingThis ? 'text-primary' : 'text-text-main'}`}>
                                                {title}
                                            </h3>
                                            {completedAudios.some(item =>
                                                (typeof item === 'string' ? item === audio.name : item.name === audio.name) &&
                                                (typeof item === 'object' && item.completed)
                                            ) && (
                                                    <CheckCircle size={14} className="text-green-500 shrink-0" />
                                                )}
                                        </div>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <span className="text-[10px] px-1.5 py-0.5 bg-dark-bg border border-dark-border rounded text-text-muted font-bold uppercase tracking-wider">
                                                MP3
                                            </span>
                                            {isLocked && (
                                                <span className="text-[10px] px-1.5 py-0.5 bg-red-500/10 text-red-600 rounded border border-red-500/10 font-bold uppercase tracking-wider">
                                                    PRO
                                                </span>
                                            )}
                                            {!isLocked && (
                                                <p className="text-xs text-text-muted">
                                                    {Math.round(audio.metadata.size / 1024 / 1024 * 10) / 10} MB
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <button
                                    className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all ${isPlayingThis
                                        ? 'bg-primary text-white shadow-lg shadow-primary/20'
                                        : 'bg-dark-bg text-text-muted border border-dark-border group-hover:border-primary group-hover:text-primary'
                                        }`}
                                >
                                    {isPlayingThis ? <Pause size={20} fill="currentColor" /> : <Play size={20} className="ml-0.5" fill={isLocked ? "none" : "currentColor"} />}
                                </button>
                            </div>
                        );
                    })}
                </div>

                {audios.length === 0 && (
                    <div className="text-center py-20 bg-dark-card rounded-3xl border border-dark-border border-dashed">
                        <Headphones size={48} className="mx-auto text-dark-border mb-4" />
                        <p className="text-text-muted font-medium">{t('audio.status.no_audios')}</p>
                    </div>
                )}

                {/* Premium Banner */}
                {!isPremium && (
                    <div className="mt-12 p-8 bg-gradient-to-br from-primary/10 to-transparent border border-primary/20 rounded-3xl relative overflow-hidden group">
                        <div className="relative z-10">
                            <h3 className="text-xl font-bold text-text-main mb-2">{t('audio.premium.title')}</h3>
                            <p className="text-text-muted text-sm mb-6 max-w-md">
                                {t('audio.premium.description')}
                            </p>
                            <Link to="/pricing" className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover transition-all">
                                {t('audio.premium.cta')}
                                <Lock size={18} />
                            </Link>
                        </div>
                        <div className="absolute top-1/2 right-8 -translate-y-1/2 opacity-10 group-hover:opacity-20 transition-opacity">
                            <Headphones size={120} />
                        </div>
                    </div>
                )}
            </div>

            {/* Persistent Mini Player */}
            {currentAudio && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[calc(100%-3rem)] max-w-2xl z-50 animate-in slide-in-from-bottom-8 duration-500">
                    <div className="bg-dark-card/90 backdrop-blur-xl border border-white/5 p-4 md:p-6 rounded-2xl shadow-2xl flex flex-col gap-4 ring-1 ring-white/10">
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-4 min-w-0">
                                <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center text-primary shrink-0 animate-pulse">
                                    <Music size={24} />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-sm font-bold text-text-main truncate pr-4">
                                        {currentAudio.name.replace('.mp3', '').replace(/^\d+[-_]/, '').replaceAll('-', ' ')}
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-primary animate-ping"></span>
                                        <p className="text-[10px] text-primary font-bold uppercase tracking-widest">{t('audio.status.playing')}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <span className="text-xs font-medium text-text-muted hidden sm:block">
                                    {formatTime(currentTime)} / {formatTime(duration)}
                                </span>
                                <button
                                    onClick={() => togglePlay(currentAudio)}
                                    className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center hover:scale-110 active:scale-95 transition-all shadow-lg shadow-primary/30"
                                >
                                    {isPlaying ? <Pause size={24} fill="currentColor" /> : <Play size={24} className="ml-1" fill="currentColor" />}
                                </button>
                            </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="flex flex-col gap-1.5">
                            <input
                                type="range"
                                min="0"
                                max={duration || 100}
                                value={currentTime}
                                onChange={handleProgressChange}
                                className="w-full h-1.5 bg-dark-bg rounded-lg appearance-none cursor-pointer accent-primary hover:accent-primary-hover transition-all"
                            />
                            <div className="flex justify-between sm:hidden">
                                <span className="text-[10px] font-medium text-text-muted">{formatTime(currentTime)}</span>
                                <span className="text-[10px] font-medium text-text-muted">{formatTime(duration)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
}
